import { InfoModal } from 'components/InfoModal/InfoModal';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { LabelValueColumn } from 'components/TableColumn/LabelValueColumn';
import { ALL_VENDOR_OPTION, ECSegmentRulesDetail, ECSegmentType } from 'core/ecSegment/ECSegment';
import { DefaultECSegmentManager, ECSegmentManager } from 'core/ecSegment/ECSegmentManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import styles from './ecSegmentRulesModal.module.scss';
import { camelCase } from 'lodash';

const ecSegmentManager: ECSegmentManager = new DefaultECSegmentManager();
export const ECSegmentRulesModal: React.FC<{
  ecSegmentId: string;
  advertiserId: number;
  onClose: () => void;
}> = ({
  ecSegmentId,
  advertiserId,
  onClose
}) => {

  const [ecSegmentRules, setECSegmentRules] = useState<ECSegmentRulesDetail>();

  const {
    loading,
    callAPIs
  } = useCallAPI();

  useEffect(() => {
    callAPIs(
      [() => ecSegmentManager.getECSegmentRules(ecSegmentId, advertiserId)],
      setECSegmentRules
    );
  }, [ecSegmentId, advertiserId, callAPIs]);

  const requiredFieldsOfTypeMap = useMemo(() => {
    const basicFields = [
      i18n.t<string>('ecSegmentForm.labels.type'),
      i18n.t<string>('ecSegmentForm.labels.durationDays')
    ];
    return {
      [ECSegmentType.SEARCH_KEYWORDS]: [
        ...basicFields,
        i18n.t<string>(`ecSegment.types.${camelCase(ECSegmentType.SEARCH_KEYWORDS)}`)
      ],
      [ECSegmentType.PRODUCT]: [
        ...basicFields,
        i18n.t<string>('ecSegmentForm.labels.eventType'),
        i18n.t<string>(`ecSegment.types.${camelCase(ECSegmentType.PRODUCT)}`)
      ],
      [ECSegmentType.PRODUCT_CATEGORY]: [
        ...basicFields,
        i18n.t<string>('ecSegmentForm.labels.vendor'),
        i18n.t<string>('ecSegmentForm.labels.eventType'),
        i18n.t<string>(`ecSegment.types.${camelCase(ECSegmentType.PRODUCT_CATEGORY)}`)
      ]
    };
  }, []);

  const ecSegmentRulesContent = useMemo(() => {
    if (!ecSegmentRules) {
      return null;
    }
    return ecSegmentRules.rules.map((rule, index) => {
      const requiredFieldsOfType = requiredFieldsOfTypeMap[rule.type];
      const inclusionSeperator = rule.type === ECSegmentType.SEARCH_KEYWORDS ? ', ' : '\n';
      const labelValueData = [{
        label: i18n.t<string>('ecSegmentForm.labels.type'),
        value: i18n.t<string>(`ecSegment.types.${camelCase(rule.type)}`)
      }, {
        label: i18n.t<string>('ecSegmentForm.labels.durationDays'),
        value: `${rule.durationDays} ${i18n.t<string>('common.units.days')}`
      }, {
        label: i18n.t<string>('ecSegmentForm.labels.vendor'),
        value: rule.vendorNumber ? rule.vendorNumber : ALL_VENDOR_OPTION.label
      }, {
        label: i18n.t<string>('ecSegmentForm.labels.eventType'),
        value: i18n.t<string>(`ecSegment.eventTypes.${camelCase(rule.eventType)}`)
      }, {
        label: i18n.t<string>(`ecSegment.types.${camelCase(rule.type)}`),
        value: rule.inclusion.join(inclusionSeperator)
      }].filter(data => requiredFieldsOfType.includes(data.label));

      return (
        <div key={index} className={styles.ecSegmentRulesContent}>
          <h2>
            {i18n.t<string>('ecSegmentRulesModal.titles.rules', { index: index + 1 })}
          </h2>
          <LabelValueColumn options={labelValueData} />
        </div>
      );
    });
  }, [ecSegmentRules, requiredFieldsOfTypeMap]);

  if (loading) {
    return <LoadingIndicator />;
  }
  return (
    <InfoModal className={styles.infoModal} onClose={onClose}>
      <div className={styles.eCSegmentRulesContent}>
        {ecSegmentRulesContent}
      </div>
    </InfoModal>
  );
};
